// Central catalog of extensions and modules requiring login from extensions/index.jsx and library_extension.jsx
export const NO_LOGIN_REQUIRED = ['python', 'blocks'];

/**
 * Hierarchical grouping of Extensions -> Sub-Modules
 * Used for the Admin Extension/Module selection hierarchy and RBAC management.
 */
export const EXTENSION_GROUPS = [
  {
    id: 'Coding',
    name: 'Coding Environments',
    description: 'Python & Block-based programming environments',
    category: 'Coding',
    modules: [
      { id: 'python', name: 'Python Coding', description: 'Python programming editor and interactive console' },
      { id: 'blocks', name: 'Scratch / Blocks', description: 'Visual drag-and-drop block coding canvas' }
    ]
  },
  {
    id: 'Robotics',
    name: 'Robotics',
    description: 'Autonomous car, spider, robotic arm, humanoid robot, and LEGO robotics',
    category: 'Robotics',
    modules: [
      { id: 'car', name: 'Robo Car', description: 'Control and code autonomous robotic car' },
      { id: 'spider', name: 'Robotic Spider', description: 'Multi-legged robotic spider locomotion' },
      { id: 'arm', name: 'Robotic Arm', description: 'Multi-axis robotic gripper and arm control' },
      { id: 'humanoidrobot', name: 'Humanoid Robot', description: 'Humanoid bipedal robot motion and sensing' },
      { id: 'ev3', name: 'LEGO MINDSTORMS EV3', description: 'Connect motors and sensors for LEGO EV3' },
      { id: 'boost', name: 'LEGO BOOST', description: 'Bring LEGO robotics creations to life' },
      { id: 'pythonRoboCar', name: 'Python Robo Car', description: 'Python programming for robotic cars' },
      { id: 'pythonRoboArm', name: 'Python Robotic Arm', description: 'Python script control for robotic arm' },
      { id: 'PythonSpider', name: 'Python Robotic Spider', description: 'Python script control for spider robot' }
    ]
  },
  {
    id: 'AI | ML',
    name: 'Artificial Intelligence & ML',
    description: 'Machine learning models, hand pose, face, speech recognition & NLP',
    category: 'Artificial Intelligence',
    modules: [
      { id: 'mlblock', name: 'Machine Learning', description: 'Train and use custom machine learning models' },
      { id: 'HumanBody', name: 'Hand Pose', description: 'Detect hand landmarks and gestures' },
      { id: 'Speech_Recognition', name: 'Speech Recognition', description: 'Voice command and speech-to-text recognition' },
      { id: 'face', name: 'Face Detection', description: 'Real-time face and expression detection' },
      { id: 'NLP', name: 'Natural Language Processing', description: 'Analyze sentiment and text with NLP' },
      { id: 'object', name: 'Object Detection', description: 'Identify everyday objects in camera feed' },
      { id: 'Chatgpt', name: 'AI Chatbot', description: 'Interactive AI conversational assistant' }
    ]
  },
  {
    id: 'IoT | Electronics',
    name: 'IoT | Electronics',
    description: 'Arduino Uno, sensors, actuators, micro:bit hardware and LEGO WeDo 2.0',
    category: 'IoT | Electronics',
    modules: [
      { id: 'arduino', name: 'Arduino Uno', description: 'Connect and burn code to Arduino Uno boards' },
      { id: 'sensors', name: 'Sensors', description: 'Analog and digital sensor integration' },
      { id: 'actuators', name: 'Actuators', description: 'Servos, motors, relays, and output devices' },
      { id: 'microbit', name: 'micro:bit', description: 'Connect micro:bit hardware and pins' },
      { id: 'wedo2', name: 'LEGO Education WeDo 2.0', description: 'Build and program motors and sensors' },
      { id: 'gdxfor', name: 'Go Direct Force & Acceleration', description: 'Vernier sensor physics measurement' }
    ]
  },
  {
    id: 'Mathematics',
    name: 'Mathematics',
    description: '3D geometry CAD, math puzzles, coordinate geometry, fractions and angles',
    category: 'Mathematics',
    modules: [
      { id: 'cad', name: 'Math 3D Shapes (CAD)', description: 'Create 3D geometric models in blocks' },
      { id: 'MathJoy', name: 'Mathematics Challenges', description: 'Interactive math calculation and puzzles' },
      { id: 'chart', name: 'Charts and Tables', description: 'Data visualization, bar charts and plots' },
      { id: 'coordinateGeometry', name: 'Co-ordinate Geometry', description: 'Cartesian coordinate plane plotting' },
      { id: 'angles', name: 'Angles', description: 'Protractor and angle calculation tools' },
      { id: 'Fractions', name: 'Fractions', description: 'Fraction visualizer and operations' },
      { id: 'puzzles', name: 'Balance Scale Puzzle', description: 'Weight balancing and logic puzzles' }
    ]
  },
  {
    id: 'Geography',
    name: 'Geography',
    description: 'Indian geography, states, maps, rivers and topography',
    category: 'Geography',
    modules: [
      { id: 'ingeo', name: 'Indian Geography', description: 'Explore states, rivers, and topography of India' }
    ]
  },
  {
    id: 'Cybersecurity',
    name: 'Cybersecurity',
    description: 'Cyber safety, digital citizenship and defense',
    category: 'Cybersecurity',
    modules: [
      { id: 'security', name: 'Cyber Security', description: 'Learn data safety and network encryption' },
      { id: 'bullying', name: 'Cyber Bullying Defense', description: 'Digital citizenship and online safety' }
    ]
  },
  {
    id: 'Web & App Development',
    name: 'Web & App Development',
    description: 'Web development editor (HTML/CSS/JS) and mobile app development',
    category: 'Web & App Development',
    modules: [
      { id: 'HtmlEditor', name: 'Web Development', description: 'Interactive HTML, CSS & JavaScript web coding environment' },
      { id: 'MITAppInventor', name: 'App Development', description: 'Mobile app development platform (MIT App Inventor)' }
    ]
  },
  {
    id: 'Environments & Tools',
    name: 'Environments & Tools',
    description: 'AR/VR, 3D printing CAD, interactive game development and drones',
    category: 'Environments & Tools',
    modules: [
      { id: 'ar_vr', name: 'AR | VR', description: 'Augmented & Virtual Reality development guides and 3D scenes' },
      { id: '3d_printing', name: '3D Printings', description: '3D CAD design tools and 3D printing preparation' },
      { id: 'game_development', name: 'Game Development', description: 'Interactive 3D game design and Unity environments' },
      { id: 'drones', name: 'Drones', description: 'Drone technology, programming, and aerodynamics' }
    ]
  },
  {
    id: 'All Extensions/Projects',
    name: 'All Extensions/Projects (Interactive Media)',
    description: 'Pen drawing, video sensing, text to speech, translation, Makey Makey & music',
    category: 'All Extensions/Projects',
    modules: [
      { id: 'pen', name: 'Pen', description: 'Draw on stage with sprites and custom lines' },
      { id: 'videoSensing', name: 'Video Sensing', description: 'Detect motion with webcam video feed' },
      { id: 'text2speech', name: 'Text to Speech', description: 'Make projects talk with voice synthesis' },
      { id: 'translate', name: 'Translate', description: 'Translate words into multiple languages' },
      { id: 'makeymakey', name: 'Makey Makey', description: 'Turn everyday objects into keyboard touch keys' },
      { id: 'music', name: 'Music Instruments', description: 'Play drum beats, piano notes, and instruments' }
    ]
  }
];

export const EXTENSION_CATEGORIES = EXTENSION_GROUPS.map(g => g.category);

export const ALL_EXTENSION_MODULES = EXTENSION_GROUPS.flatMap(group =>
  group.modules.map(mod => ({
    ...mod,
    category: group.category,
    extensionId: group.id,
    extensionName: group.name
  }))
);

// Normalized aliases map for cross-compatibility
const ALIAS_MAP = {
  'web_development': 'HtmlEditor',
  'htmleditor': 'HtmlEditor',
  'html_editor': 'HtmlEditor',
  'web_dev': 'HtmlEditor',
  'web development': 'HtmlEditor',
  'app_development': 'MITAppInventor',
  'mitappinventor': 'MITAppInventor',
  'mit_app_inventor': 'MITAppInventor',
  'mit app inventor': 'MITAppInventor',
  'app development': 'MITAppInventor',
  'ar_vr': 'ar_vr',
  'arvr': 'ar_vr',
  'ar | vr': 'ar_vr',
  '3d_printing': '3d_printing',
  '3d printings': '3d_printing',
  '3d_printings': '3d_printing',
  '3dprintings': '3d_printing',
  'design': '3d_printing',
  'game_development': 'game_development',
  'game development': 'game_development',
  'unity': 'game_development',
  'drones': 'drones'
};

/**
 * Check if a given extensionId is accessible for the current user
 * @param {string} extensionId
 * @returns {boolean}
 */
export const isExtensionAccessible = (extensionId) => {
  if (!extensionId) return true;
  
  // Extensions that never require login
  if (NO_LOGIN_REQUIRED.includes(extensionId)) {
    return true;
  }

  // If user is NOT logged in: ALL modules are visible/accessible to browse
  const token = localStorage.getItem('accessToken');
  if (!token) {
    return true;
  }

  // If logged in as Admin: full access to everything
  const role = localStorage.getItem('userRole');
  if (role === 'admin') {
    return true;
  }

  // If logged in as regular user: check assigned modules list
  try {
    const rawModules = localStorage.getItem('accessibleModules');
    if (!rawModules) return false;
    const modules = typeof rawModules === 'string' ? JSON.parse(rawModules) : rawModules;
    if (!Array.isArray(modules)) return false;

    if (modules.includes('*') || modules.includes('all')) {
      return true;
    }

    if (modules.includes(extensionId)) {
      return true;
    }

    // Check alias mapping
    const targetKey = String(extensionId).toLowerCase();
    const standardId = ALIAS_MAP[targetKey] || extensionId;
    if (modules.includes(standardId)) {
      return true;
    }

    // Also check if any module in user's list maps to the same standardId
    for (const mod of modules) {
      const modStd = ALIAS_MAP[String(mod).toLowerCase()] || mod;
      if (modStd === standardId || modStd === extensionId) {
        return true;
      }
    }

    return false;
  } catch (e) {
    return false;
  }
};
